import React, { useState, useEffect } from 'react'
import ReactCountryFlag from "react-country-flag"
import { useAuth } from "./api/authStore"
import useMobile from "./hooks/useMobile"
import moment from 'moment'
import LoadingSpinner from './components/LoadingSpinner'

// Компонент тултипа (мобильно-дружественный)
const Tooltip = ({ children, title, onLongPress, isCheckingSpeed }) => {
    const [show, setShow] = useState(false)
    const [isMobile, setIsMobile] = useState(false)
    const [touchTimer, setTouchTimer] = useState(null)
    
    // Определяем, мобильное ли устройство
    React.useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768 || 'ontouchstart' in window)
        }
        checkMobile()
        window.addEventListener('resize', checkMobile)
        return () => window.removeEventListener('resize', checkMobile)
    }, [])
    
    const handleTouchStart = (e) => {
        if (isMobile) {
            // Короткое касание - показываем тултип
            setShow(true)
            setTimeout(() => setShow(false), 3000)
            
            // Длительное зажатие - запускаем проверку скорости
            const timer = setTimeout(() => {
                if (onLongPress) {
                    console.log('Long press triggered in ConfirmDialog!') // Отладка
                    onLongPress(e)
                }
            }, 600) // 600ms для длительного нажатия
            
            setTouchTimer(timer)
        }
    }
    
    const handleTouchEnd = (e) => {
        if (isMobile && touchTimer) {
            // Если отпустили до истечения 600ms, отменяем длительное нажатие
            clearTimeout(touchTimer)
            setTouchTimer(null)
        }
    }
    
    const handleTouchCancel = (e) => {
        if (isMobile && touchTimer) {
            clearTimeout(touchTimer)
            setTouchTimer(null)
        }
    }
    
    return (
        <div 
            className={`shop-confirm-tooltip-wrap${isMobile ? ' shop-confirm-tooltip-wrap--block' : ''}`}
            style={{ 
                userSelect: 'none',
                WebkitUserSelect: 'none',
                MozUserSelect: 'none',
                msUserSelect: 'none',
                WebkitTouchCallout: 'none',
                WebkitTapHighlightColor: 'transparent'
            }}
            onMouseEnter={() => !isMobile && setShow(true)}
            onMouseLeave={() => !isMobile && setShow(false)}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={handleTouchCancel}
        >
            {/* Показываем спиннер только если идет проверка скорости */}
            {isCheckingSpeed ? (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <LoadingSpinner size={22} />
                </div>
            ) : (
                children
            )}
            {show && (
                <div className={`shop-confirm-tooltip${isMobile ? ' shop-confirm-tooltip--mobile' : ''}`}>
                    {title}
                </div>
            )}
        </div>
    )
}


// Компонент для уведомлений
const CustomSnackbar = ({ open, onClose, message, severity = 'success' }) => {
    useEffect(() => {
        if (open) {
            const timer = setTimeout(() => {
                onClose()
            }, 4000)
            
            return () => clearTimeout(timer)
        }
    }, [open, onClose])

    if (!open) return null

    const icon = severity === 'error' ? '✕' : severity === 'success' ? '✓' : '!'

    return (
        <div className={`shop-toast shop-toast--animated shop-toast--${severity}`}>
            <span className="shop-toast__icon">{icon}</span>
            <span className="shop-toast__message">{message}</span>
            <button type="button" onClick={onClose} className="shop-toast__close">✕</button>
        </div>
    )
}

const formatFraudScore = (value) => {
    if (value === null || value === undefined || value === '') {
        return '-'
    }
    return value
}

const getProxyFraudScores = (proxy) => {
    const source = proxy?.riskscore
    if (!source) {
        return null
    }
    const hasAnyScore = ['getipintel', 'ipqs', 'maxmind', 'scamalytics'].some(
        (key) => source[key] !== null && source[key] !== undefined
    )
    return hasAnyScore ? source : null
}

const ConfirmDialog = (props) => {
    const { getAccessTokenSilently } = useAuth()
    const { isMobile } = useMobile()

    const selectedProxy = props.selectedProxy

    const [openBuyError, setBuyError] = useState(false)
    const [openBuySuccess, setBuySuccess] = useState(false)
    const [alreadyPurchased, setAlreadyPutchased] = useState(false)
    const [isCheckingSpeed, setIsCheckingSpeed] = useState(false)
    const [proxySpeed, setProxySpeed] = useState(null)
    const [speedCheckTime, setSpeedCheckTime] = useState(null)
    const [fraudScores, setFraudScores] = useState(null)
    const [isCheckingFraud, setIsCheckingFraud] = useState(false)
    const [fraudCheckError, setFraudCheckError] = useState(false)

    // Инициализация скорости из данных прокси
    useEffect(() => {
        if (selectedProxy?.speed?.bytesPerSec) {
            const speed = (selectedProxy.speed.bytesPerSec / 1000 / 1000).toFixed(2)
            setProxySpeed(speed)
            setSpeedCheckTime(selectedProxy.speed.checkedAt)
        } else {
            setProxySpeed(null)
        }
        setFraudScores(getProxyFraudScores(selectedProxy))
    }, [selectedProxy])

    const handleClose = () => {
        props.setConfirmOpen(false)
    }

    useEffect(() => {
        if (!props.open) return

        const handleEsc = (e) => {
            if (e.key === 'Escape') {
                props.setConfirmOpen(false)
            }
        }

        window.addEventListener('keydown', handleEsc)
        return () => window.removeEventListener('keydown', handleEsc)
    }, [props.open, props.setConfirmOpen])

    const handleBuyPrivate = async() => {
        try {
            const proxy = {
                proxyid: selectedProxy.proxyid,
                private: true,
                price: selectedProxy?.purchase?.private,
            }
            
            await props.apiClient.setToken(getAccessTokenSilently()).buyProxy(proxy)

            props.setConfirmOpen(false)
            setBuySuccess(true)
            props.setAfterBuy(true)
            props.refreshUserData?.()
        } catch (e) {
            if(e.response.data.error === 'paramInvalid'){

            } else if (e.response.data.error === 'agentAlreadyLeased'){
                setAlreadyPutchased(true)
            } else {
                setBuyError(true)
            }

            props.setConfirmOpen(false)
            
        }
    }

    const handleBuyShared = async() => {
        try {
            const proxy = {
                proxyid: selectedProxy.proxyid,
                private: false,
                price: selectedProxy?.purchase?.shared,
            }

            await props.apiClient.setToken(getAccessTokenSilently()).buyProxy(proxy)

            props.setConfirmOpen(false)
            setBuySuccess(true)
            props.setAfterBuy(true)
            props.refreshUserData?.()
        } catch (e) {
            if(e.response && e.response.data && e.response.data.error === 'paramInvalid'){

            } else if (e.response && e.response.data && e.response.data.error === 'agentAlreadyLeased'){
                setAlreadyPutchased(true)
            } else {
                setBuyError(true)
            }

            props.setConfirmOpen(false)
        }
    }
    
    const handleBuyErrorClose = () => {
        setBuyError(false)
    }

    const handleBuySuccessClose = () => {
        setBuySuccess(false)
    }

    const handleAlreadyPurchasedClose = () => {
        setAlreadyPutchased(false)
    }

    const handleSpeedCheck = async () => {
        try {
            setIsCheckingSpeed(true)
            setProxySpeed(null)

            const proxyid = selectedProxy?.proxyid
            if (!proxyid) {
                setProxySpeed('Error')
                return
            }
            
            const response = await props.apiClient.setToken(getAccessTokenSilently()).checkProxySpeed(proxyid)
            const speed = (response?.bytesPerSec != null) ? (response.bytesPerSec / 1000 / 1000).toFixed(2) : null
            
            setProxySpeed(speed)
            setSpeedCheckTime(response?.checkedAt)
        } catch (e) {
            console.error('Speed check error:', e)
            setProxySpeed('Error')
        } finally {
            setIsCheckingSpeed(false)
        }
    }

    const handleFraudCheck = async () => {
        try {
            setIsCheckingFraud(true)
            setFraudCheckError(false)

            const proxyid = selectedProxy?.proxyid
            if (!proxyid) {
                setFraudCheckError(true)
                return
            }

            const response = await props.apiClient
                .setToken(getAccessTokenSilently())
                .checkProxyFraudScore(proxyid)

            setFraudScores(response?.riskscore ?? null)
            props.refreshUserData?.()
        } catch (e) {
            console.error('Fraud score check error:', e)
            setFraudCheckError(true)
        } finally {
            setIsCheckingFraud(false)
        }
    }

    const handleFraudCheckErrorClose = () => {
        setFraudCheckError(false)
    }

    const maskedIp = selectedProxy?.ip?.replace(/_/g, '*') || 'N/A'

    const fraudScoreRows = [
        { label: 'Getipintel', key: 'getipintel' },
        { label: 'MaxMind', key: 'maxmind' },
        { label: 'Scamalytics', key: 'scamalytics' },
    ]

    return (
        <>
            {/* Модальное окно подтверждения покупки */}
            {props.open && (
                <div className="shop-confirm-overlay" onClick={handleClose}>
                    <div className="shop-confirm-modal shop-confirm-modal--scroll shop-confirm-modal--sidebar shop-confirm-modal--purchase" onClick={(e) => e.stopPropagation()}>
                        
                        {isMobile ? (
                            <button
                                type="button"
                                onClick={handleClose}
                                className="shop-confirm-btn-secondary shop-confirm-btn-secondary--full shop-confirm-close-mobile"
                                aria-label="Close"
                            >
                                Close
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={handleClose}
                                className="shop-confirm-close"
                                aria-label="Close"
                            >
                                ✕
                            </button>
                        )}

                        <h2 className="shop-confirm-title">
                            Proxy Purchase Confirmation
                        </h2>

                        <div className="shop-confirm-panel">
                            {/* Заголовок с флагом */}
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--spacing-md)',
                                marginBottom: 'var(--spacing-md)'
                            }}>
                                <div style={{
                                    width: '30px',
                                    height: '24px',
                                    borderRadius: 'var(--radius-sm)',
                                    overflow: 'hidden',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <ReactCountryFlag 
                                        countryCode={selectedProxy?.geo?.cc} 
                                        svg 
                                        style={{ width: '100%', height: '100%' }}
                                    />
                                </div>
                                <div>
                                    <h3 className="shop-confirm-panel__heading">
                                        {selectedProxy?.geo?.cc} • Proxy Info
                                    </h3>
                                    <p className="shop-confirm-panel__sub">
                                        {selectedProxy?.geo?.reg} • {selectedProxy?.geo?.city} • {selectedProxy?.geo?.zip} • {selectedProxy?.geo?.isp}
                                    </p>
                                </div>
                            </div>

                            {/* IP и fraud score */}
                            <div
                                className="shop-confirm-panel__divider"
                                style={{
                                display: 'flex',
                                gap: 'var(--spacing-md)',
                                alignItems: isMobile ? 'stretch' : 'flex-start',
                                flexDirection: isMobile ? 'column' : 'row',
                            }}>
                                <div style={{
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '4px',
                                    width: isMobile ? '100%' : 'auto',
                                }}>
                                    <div className="shop-confirm-row">
                                        <span className="shop-confirm-muted">IP Address:</span>{' '}
                                        <span className="shop-confirm-mono">
                                            {maskedIp}
                                        </span>
                                    </div>
                                    {fraudScoreRows.map(({ label, key }) => (
                                        <div key={key} className="shop-confirm-row">
                                            <span className="shop-confirm-muted">{label}:</span>{' '}
                                            <span className="shop-confirm-mono">
                                                {formatFraudScore(fraudScores?.[key])}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                                <button
                                    type="button"
                                    onClick={handleFraudCheck}
                                    disabled={isCheckingFraud}
                                    className={`shop-confirm-btn-secondary${isMobile ? ' shop-confirm-btn-secondary--full' : ''}`}
                                >
                                    {isCheckingFraud ? (
                                        <LoadingSpinner size={22} />
                                    ) : (
                                        'check $0.05'
                                    )}
                                </button>
                            </div>

                        </div>

                        {/* Информация о покупке */}
                        <div style={{ marginBottom: 'var(--spacing-xl)' }}>
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 'var(--spacing-md)'
                            }}>
                                <p className="shop-confirm-body-text">
                                    The proxy will be rented by you for <strong className="shop-confirm-accent">24 hours</strong>.
                                </p>
                                
                                <p className="shop-confirm-body-text">
                                    You can refund proxy if it doesn't work in first <strong className="shop-confirm-accent">1 hour</strong>.
                                </p>
                                
                                <p className="shop-confirm-body-muted">
                                    Confirming purchase you agree with our{' '}
                                    <a 
                                        href="https://xsocks.pro/terms-conditions.html"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="shop-confirm-link"
                                    >
                                        terms and conditions
                                    </a>
                                </p>
                            </div>
                        </div>

                        {/* Кнопки */}
                        <div className="shop-confirm-actions" style={{
                            display: 'flex',
                            gap: 'var(--spacing-md)',
                            justifyContent: 'center',
                            flexDirection: isMobile ? 'column' : 'row'
                        }}>
                            {!isMobile && (
                                isCheckingSpeed ? (
                                    <button
                                        type="button"
                                        className="btn btn-outline"
                                        style={{ 
                                            minWidth: '140px',
                                            height: '32px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            opacity: 0.7,
                                            cursor: 'not-allowed'
                                        }}
                                        disabled
                                    >
                                        <LoadingSpinner size={22} />
                                    </button>
                                ) : (
                                    <Tooltip 
                                        title={speedCheckTime ? moment(speedCheckTime).fromNow() : 'Speed test result'}
                                    >
                                        <button
                                            type="button"
                                            onClick={handleSpeedCheck}
                                            className="btn btn-outline"
                                            style={{ 
                                                minWidth: '140px',
                                                height: '32px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '8px',
                                            }}
                                        >
                                            {proxySpeed != null ? `${proxySpeed} Mbps` : 'Check speed'}
                                        </button>
                                    </Tooltip>
                                )
                            )}
                            
                            {/* На мобильных устройствах показываем только результат скорости */}
                            {isMobile && (
                                <div className="shop-confirm-speed-mobile" style={{ marginBottom: 'var(--spacing-sm)' }}>
                                    <Tooltip 
                                        title={speedCheckTime ? moment(speedCheckTime).fromNow() : 'Hold to test speed'}
                                        onLongPress={() => {
                                          handleSpeedCheck()
                                        }}
                                        isCheckingSpeed={isCheckingSpeed}
                                    >
                                        <span>
                                            {proxySpeed != null ? `${proxySpeed} Mbps` : 'Check speed'}
                                        </span>
                                    </Tooltip>
                                </div>
                            )}
                            {selectedProxy?.purchase?.private !== undefined ? (
                                <>
                                    <button
                                        onClick={handleBuyShared}
                                        className="btn btn-buy"
                                        style={{ 
                                            minWidth: isMobile ? '100%' : '140px', 
                                            height: '32px',
                                            marginBottom: isMobile ? 'var(--spacing-sm)' : '0'
                                        }}
                                    >
                                        Buy Shared ${selectedProxy?.purchase?.shared ? (selectedProxy.purchase.shared/100).toFixed(2) : '0.00'} 
                                    </button>
                                    <button
                                        onClick={handleBuyPrivate}
                                        className="btn btn-buy"
                                        style={{ 
                                            minWidth: isMobile ? '100%' : '140px', 
                                            height: '32px'
                                        }}
                                    >
                                        Buy Private ${selectedProxy?.purchase?.private ? (selectedProxy.purchase.private/100).toFixed(2) : '0.00'} 
                                    </button>
                                </>
                            ) : (
                                <button
                                    onClick={handleBuyShared}
                                    className="btn btn-buy"
                                    style={{ 
                                        minWidth: isMobile ? '100%' : '150px', 
                                        height: '32px'
                                    }}
                                >
                                    Buy Shared ${selectedProxy?.purchase?.shared ? (selectedProxy.purchase.shared/100).toFixed(2) : '0.00'} 
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Уведомления */}
            <CustomSnackbar
              open={openBuyError}
              onClose={handleBuyErrorClose}
                message="Error purchasing proxy."
                severity="error"
            />

            <CustomSnackbar
              open={alreadyPurchased}
              onClose={handleAlreadyPurchasedClose}
                message="Current proxy was already purchased."
                severity="warning"
            />

            <CustomSnackbar
              open={openBuySuccess}
              onClose={handleBuySuccessClose}
                message="Proxy purchased successfully!"
              severity="success"
            />

            <CustomSnackbar
              open={fraudCheckError}
              onClose={handleFraudCheckErrorClose}
              message="Error checking fraud score."
              severity="error"
            />
        </>
    )
}

export default ConfirmDialog